import { useCallback } from 'react';
import { useJobHours } from '@/hooks/useJobs';
import { APIError } from '@/api/client';
import { authClient } from '@/lib/auth-client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { JobHoursReport } from '@jobos/shared/types/reports';

interface JobHoursModalProps {
  jobId: string;
  jobCode: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function buildPrintableHtml(data: JobHoursReport, jobCode: string): string {
  const dateRange = `${new Date(data.summary.dateRange.start).toLocaleDateString()} - ${new Date(data.summary.dateRange.end).toLocaleDateString()}`;

  const workerRows = data.byWorker
    .map(
      (w) => `
      <tr>
        <td>${w.workerName}</td>
        <td class="right">${w.eventCount}</td>
        <td class="right">${w.totalHours.toFixed(2)}</td>
      </tr>`
    )
    .join('');

  const eventRows = data.events
    .map(
      (e) => `
      <tr>
        <td>${e.date}</td>
        <td>${e.timeRange}</td>
        <td>${e.worker}</td>
        <td>${e.eventName}</td>
        <td class="right">${e.hours.toFixed(2)}</td>
      </tr>`
    )
    .join('');

  return `<!DOCTYPE html>
<html>
<head>
  <title>Job Hours Report - ${jobCode}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 24px; color: #111; font-size: 12px; }
    h1 { font-size: 20px; margin-bottom: 4px; }
    .subtitle { color: #666; margin-bottom: 20px; font-size: 13px; }
    .summary { display: flex; gap: 24px; margin-bottom: 24px; padding: 12px 0; border-top: 2px solid #111; border-bottom: 1px solid #ccc; }
    .summary-item label { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #666; margin-bottom: 2px; }
    .summary-item .value { font-size: 18px; font-weight: 700; }
    .summary-item .value.small { font-size: 13px; font-weight: 600; }
    h2 { font-size: 14px; margin-bottom: 8px; margin-top: 20px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #666; padding: 6px 8px; border-bottom: 2px solid #ddd; }
    td { padding: 5px 8px; border-bottom: 1px solid #eee; }
    .right { text-align: right; }
    th.right { text-align: right; }
    @media print {
      body { padding: 0; }
      @page { margin: 1.5cm; }
    }
  </style>
</head>
<body>
  <h1>Job Hours Report</h1>
  <div class="subtitle">Job ${jobCode} &mdash; ${dateRange}</div>

  <div class="summary">
    <div class="summary-item">
      <label>Total Hours</label>
      <div class="value">${data.summary.totalHours.toFixed(2)}</div>
    </div>
    <div class="summary-item">
      <label>Total Events</label>
      <div class="value">${data.summary.eventCount}</div>
    </div>
    <div class="summary-item">
      <label>Date Range</label>
      <div class="value small">${dateRange}</div>
    </div>
  </div>

  ${data.byWorker.length > 0 ? `
  <h2>Hours by Worker</h2>
  <table>
    <thead>
      <tr>
        <th>Worker</th>
        <th class="right">Events</th>
        <th class="right">Total Hours</th>
      </tr>
    </thead>
    <tbody>${workerRows}</tbody>
  </table>
  ` : ''}

  ${data.events.length > 0 ? `
  <h2>Events Detail</h2>
  <table>
    <thead>
      <tr>
        <th>Date</th>
        <th>Time Range</th>
        <th>Worker</th>
        <th>Event</th>
        <th class="right">Hours</th>
      </tr>
    </thead>
    <tbody>${eventRows}</tbody>
  </table>
  ` : '<p>No events found for this job.</p>'}
</body>
</html>`;
}

export function JobHoursModal({ jobId, jobCode, open, onOpenChange }: JobHoursModalProps) {
  const { data, isLoading, error } = useJobHours(open ? jobId : undefined);

  const handlePrint = useCallback(() => {
    if (!data) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(buildPrintableHtml(data, jobCode));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }, [data, jobCode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" mobileFullscreen>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Job Hours Report</DialogTitle>
            {data && (
              <Button variant="outline" size="sm" onClick={handlePrint} className="mr-6">
                Print Report
              </Button>
            )}
          </div>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading hours data...</div>
          </div>
        )}

        {error && (
          error instanceof APIError && error.code === 'GOOGLE_TOKEN_EXPIRED' ? (
            <Alert variant="destructive" className="my-4">
              <AlertTitle>Google session expired</AlertTitle>
              <AlertDescription className="mt-2">
                <p>Your Google Calendar connection has expired. Please sign in with Google again to reconnect.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => authClient.signIn.social({ provider: 'google', callbackURL: window.location.href })}
                >
                  Reconnect Google Account
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            <div className="text-destructive py-4">
              Error loading hours: {error.message}
            </div>
          )
        )}

        {data && (
          <div className="space-y-6">
            {/* Calendar Errors Warning */}
            {data.calendarErrors && data.calendarErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertTitle>Some calendars could not be loaded</AlertTitle>
                <AlertDescription>
                  <ul className="mt-1 list-disc pl-4 text-sm">
                    {data.calendarErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Summary Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data.summary.totalHours.toFixed(2)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Total Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data.summary.eventCount}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Date Range</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm">
                    {new Date(data.summary.dateRange.start).toLocaleDateString()} - {new Date(data.summary.dateRange.end).toLocaleDateString()}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* By Worker Section */}
            {data.byWorker.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Hours by Worker</CardTitle>
                  <CardDescription>Total hours worked by each team member</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Worker</TableHead>
                        <TableHead className="text-right">Events</TableHead>
                        <TableHead className="text-right">Total Hours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.byWorker.map((worker) => (
                        <TableRow key={worker.workerName}>
                          <TableCell className="font-medium">{worker.workerName}</TableCell>
                          <TableCell className="text-right">{worker.eventCount}</TableCell>
                          <TableCell className="text-right">{worker.totalHours.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Events Section */}
            <Card>
              <CardHeader>
                <CardTitle>Events Detail</CardTitle>
                <CardDescription>All calendar events for this job</CardDescription>
              </CardHeader>
              <CardContent>
                {data.events.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No events found for this job
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Time Range</TableHead>
                        <TableHead>Worker</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.events.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>{event.date}</TableCell>
                          <TableCell className="text-sm">{event.timeRange}</TableCell>
                          <TableCell>{event.worker}</TableCell>
                          <TableCell className="max-w-xs truncate">{event.eventName}</TableCell>
                          <TableCell className="text-right">{event.hours.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
